import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const LOGIN_COPY = {
  fr: {
    fallbackError: "Une erreur est survenue. Veuillez réessayer.",
    success: "Connexion réussie!",
    invalid: "Email ou mot de passe incorrect",
    title: "Connexion",
    subtitle: "Accédez à votre espace personnel",
    email: "Email",
    emailPlaceholder: "votre@email.com",
    password: "Mot de passe",
    forgotPassword: "Mot de passe oublié ?",
    loading: "Connexion...",
    submit: "Se connecter",
    noAccount: "Pas encore de compte ?",
    register: "S'inscrire",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
  },
  en: {
    fallbackError: "Something went wrong. Please try again.",
    success: "Logged in successfully!",
    invalid: "Incorrect email or password",
    title: "Login",
    subtitle: "Access your personal account",
    email: "Email",
    emailPlaceholder: "your@email.com",
    password: "Password",
    forgotPassword: "Forgot password?",
    loading: "Logging in...",
    submit: "Log in",
    noAccount: "No account yet?",
    register: "Sign up",
    showPassword: "Show password",
    hidePassword: "Hide password",
  },
};

const formatApiErrorDetail = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const { login } = useAuth();
  const { language } = useLanguage();
  const copy = LOGIN_COPY[language] || LOGIN_COPY.fr;
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success(copy.success);
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, copy.fallbackError) || copy.invalid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <div className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-[#18181B] border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black uppercase text-white mb-2">{copy.title}</h1>
              <p className="text-zinc-500">{copy.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-white text-sm font-medium">{copy.email}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                  placeholder={copy.emailPlaceholder}
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-white text-sm font-medium">{copy.password}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] pr-10"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    aria-label={showPassword ? copy.hidePassword : copy.showPassword}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-[#CCFF00] hover:underline">
                  {copy.forgotPassword}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black py-3 font-bold uppercase tracking-wide hover:bg-[#AACC00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {loading ? copy.loading : copy.submit}
              </button>
            </form>

            <p className="text-center mt-6 text-zinc-500 text-sm">
              {copy.noAccount}{" "}
              <Link to="/register" className="text-[#CCFF00] hover:underline" data-testid="login-register-link">
                {copy.register}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginPage;
